import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { getImageUrl } from '@/utils/imagePaths';
import NotificacionesPush from '@/components/NotificacionesPush';

const Profile: React.FC = () => {
  const { user, updateUser } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingAvatar, setSavingAvatar] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [phone, setPhone] = useState(user?.phone || '');
  const [savingProfile, setSavingProfile] = useState(false);

  const avatarSrc = user?.avatar ? getImageUrl(user.avatar) : '';

  const handleAvatarSelect = (file?: File) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Selecciona una imagen válida');
      return;
    }
    setSelectedAvatar(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const handleAvatarUpload = async () => {
    if (!selectedAvatar) return;
    try {
      setSavingAvatar(true);
      const formData = new FormData();
      formData.append('avatar', selectedAvatar);
      const response = await axios.post('/api/users/me/avatar', formData, {
        timeout: 10000
      });
      if (response.data?.user) {
        updateUser(response.data.user);
      } else if (response.data?.avatar) {
        updateUser({ avatar: response.data.avatar });
      }
      toast.success('Avatar actualizado');
      setSelectedAvatar(null);
      setAvatarPreview(null);
    } catch (error: any) {
      console.error('Error guardando avatar:', error);
      toast.error(error.response?.data?.error || 'No se pudo guardar el avatar');
    } finally {
      setSavingAvatar(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    try {
      setChangingPassword(true);
      await axios.post('/api/auth/change-password', {
        currentPassword,
        newPassword
      });
      toast.success('Contraseña actualizada');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      const message = error?.response?.data?.error || 'No se pudo cambiar la contraseña';
      toast.error(message);
    } finally {
      setChangingPassword(false);
    }
  };

  useEffect(() => {
    return () => {
      if (avatarPreview) {
        URL.revokeObjectURL(avatarPreview);
      }
    };
  }, [avatarPreview]);

  useEffect(() => {
    setPhone(user?.phone || '');
  }, [user?.phone]);

  const handleProfileSave = async () => {
    const nextPhone = phone.trim();
    const currentPhone = user?.phone || '';
    if (nextPhone === currentPhone) {
      toast.error('No hay cambios para guardar');
      return;
    }
    try {
      setSavingProfile(true);
      const response = await axios.put('/api/users/me', {
        phone: nextPhone
      });
      if (response.data?.user) {
        updateUser(response.data.user);
      }
      toast.success('Perfil actualizado');
    } catch (error: any) {
      console.error('Error guardando perfil:', error);
      toast.error(error.response?.data?.error || 'No se pudo guardar el perfil');
    } finally {
      setSavingProfile(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="max-w-5xl mx-auto space-y-5 pb-10">
      <div className="space-y-2">
        <h1 className="text-2xl font-bold">Mi perfil</h1>
        <p className="text-sm text-muted-foreground">Gestioná tu avatar, tu contraseña y las notificaciones de este dispositivo.</p>
      </div>

      <div className="grid gap-5">
        <section className="rounded-lg border border-border/40 bg-background p-5 shadow-sm">
          <div className="flex items-start gap-4 flex-wrap">
            <Avatar className="h-16 w-16 border">
              {(avatarPreview || avatarSrc) && (
                <AvatarImage src={avatarPreview || avatarSrc} alt={user.firstName} />
              )}
              <AvatarFallback className="text-lg font-semibold">{`${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`}</AvatarFallback>
            </Avatar>
            <div className="space-y-3">
              <div>
                <h2 className="text-lg font-semibold leading-tight">Avatar</h2>
                <p className="text-xs text-muted-foreground">JPG, PNG o WEBP. Guardalo desde esta sección.</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleAvatarSelect(e.target.files?.[0])}
                  disabled={savingAvatar}
                  className="w-full max-w-xs"
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleAvatarUpload}
                  disabled={savingAvatar || !selectedAvatar}
                >
                  {savingAvatar ? 'Guardando...' : 'Guardar avatar'}
                </Button>
              </div>
            </div>
          </div>
        </section>

        <NotificacionesPush />

        <section className="rounded-lg border border-border/40 bg-background p-5 shadow-sm">
          <div className="space-y-4">
            <div className="space-y-1">
              <h2 className="text-lg font-semibold leading-tight">Cambiar contraseña</h2>
              <p className="text-xs text-muted-foreground">Solo tú puedes cambiarla desde aquí.</p>
            </div>
            <form className="space-y-3" onSubmit={handlePasswordChange}>
              <div className="space-y-2">
                <Label htmlFor="currentPassword">Contraseña actual</Label>
                <Input
                  id="currentPassword"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="newPassword">Nueva contraseña</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirmar contraseña</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
              <Button type="submit" disabled={changingPassword} className="w-full">
                {changingPassword ? 'Guardando...' : 'Guardar cambios'}
              </Button>
            </form>
          </div>
        </section>

        <section className="rounded-lg border border-border/40 bg-background p-5 shadow-sm">
          <div className="space-y-3">
            <div>
              <h2 className="text-lg font-semibold leading-tight">Datos del perfil</h2>
              <p className="text-xs text-muted-foreground">Actualiza el numero para WhatsApp.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Numero de WhatsApp</Label>
              <Input
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Ej: 11 5555-5555"
              />
            </div>
            <Button type="button" onClick={handleProfileSave} disabled={savingProfile}>
              {savingProfile ? 'Guardando...' : 'Guardar perfil'}
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Profile;



